// context/AuthContext.tsx

import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
} from "react"
import { LoginResponse } from "../../infrastructure/models/login"
import { SessionService } from "../../infrastructure/services/session"
import { Logger } from "../../infrastructure/services/logger"
import { ApiService } from "../../infrastructure/services/api"
import { container } from "../../infrastructure/container"

// Defining the structure of the context
interface AuthContextType {
  isLoggedIn: boolean
  login: (response: LoginResponse) => void
  logout: () => void
  userCode: string | null
  clearUserCode: () => void
}

// Create the AuthContext with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * AuthProvider wraps the app and provides authentication state.
 * It allows other components to access authentication status and actions like login and logout.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")
  const sessionService = container.resolve<SessionService>("sessionService")

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    sessionService.isLoggedin
  )
  const [userCode, setUserCode] = useState<string | null>(null)

  useEffect(() => {
    setIsLoggedIn(sessionService.isLoggedin)
  }, [sessionService.isLoggedin])

  /**
   * Function to log the user in and set the user data in the state and cookies.
   *
   * @param {LoginResponse} response - The login response
   */
  const login = (response: LoginResponse) => {
    // Only store user data for existing users
    if (response.user) {
      sessionService.user = response.user
      sessionService.terminals = response.terminals
    }
    if (response.code) {
      setUserCode(response.code) // Store the code only if it exists
    }
    setIsLoggedIn(true)
  }

  /**
   * Logout function that clears authentication and removes the cookie.
   */
  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      logger.error("Logout failed:", error)
    } finally {
      // Clear session data
      sessionService.resetStorage()
      setIsLoggedIn(false)
      setUserCode(null)
    }
  }

  const clearUserCode = () => {
    setUserCode(null) // Clear the code after password change
  }

  return (
    <AuthContext.Provider
      value={{ isLoggedIn, login, logout, userCode, clearUserCode }}
    >
      {children} {/* Render child components */}
    </AuthContext.Provider>
  )
}

/**
 * Custom hook to access the authentication context.
 *
 * @returns {AuthContextType} - The current authentication state and methods.
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}